/**
 * Hearts — which items the signed-in member has saved or follows, so the same
 * item shows the same heart everywhere (cards, map pop-ups, detail pages and
 * the existing Save buttons, which share the same saved list).
 */

import { create } from 'zustand';
import { api } from '@/lib/api';

export type HeartType = 'pin' | 'artwork' | 'route' | 'article' | 'profile' | 'event' | 'video' | 'author';

interface HeartState {
  keys: Set<string>;
  loadedFor: string | null;
  pending: Set<string>;
  load: (userId: string | null | undefined) => Promise<void>;
  has: (type: HeartType, id: string) => boolean;
  toggle: (type: HeartType, id: string) => Promise<boolean | null>;
  /** Keep in step after something else (e.g. a Save button) changed an item. */
  set: (type: HeartType, id: string, saved: boolean) => void;
  reset: () => void;
}

const k = (type: string, id: string) => `${type}:${id}`;

export const useHeartStore = create<HeartState>((set, get) => ({
  keys: new Set(),
  loadedFor: null,
  pending: new Set(),
  load: async (userId) => {
    if (!userId) { get().reset(); return; }
    if (get().loadedFor === userId) return;
    set({ loadedFor: userId });
    try {
      const list = await api.get<string[]>('/interests/keys');
      set({ keys: new Set(Array.isArray(list) ? list : []) });
    } catch { set({ loadedFor: null }); }
  },
  has: (type, id) => get().keys.has(k(type, id)),
  toggle: async (type, id) => {
    const key = k(type, id);
    if (get().pending.has(key)) return null;
    const was = get().keys.has(key);
    const optimistic = new Set(get().keys);
    if (was) optimistic.delete(key); else optimistic.add(key);
    set({ keys: optimistic, pending: new Set(get().pending).add(key) });
    try {
      const r = await api.post<{ saved: boolean; also?: { type: HeartType; id: string; saved: boolean } }>('/interests/toggle', { item_type: type, item_id: id });
      const next = new Set(get().keys);
      if (r.saved) next.add(key); else next.delete(key);
      if (r.also) { if (r.also.saved) next.add(k(r.also.type, r.also.id)); else next.delete(k(r.also.type, r.also.id)); }
      set({ keys: next });
      return r.saved;
    } catch {
      const back = new Set(get().keys);
      if (was) back.add(key); else back.delete(key);
      set({ keys: back });
      return null;
    } finally {
      const p = new Set(get().pending); p.delete(key); set({ pending: p });
    }
  },
  set: (type, id, saved) => {
    const next = new Set(get().keys);
    if (saved) next.add(k(type, id)); else next.delete(k(type, id));
    set({ keys: next });
  },
  reset: () => set({ keys: new Set(), loadedFor: null }),
}));
